
import Vue from 'vue';
import { mapState } from 'vuex';
import Header from '@root/common/components/layout/header/Header.vue';
import HeaderBottom from '@root/common/components/layout/header/HeaderBottom.vue';
import HeaderPromotion from '@root/common/components/layout/header/HeaderPromotion.vue';
import Footer from '@root/common/components/layout/footer/Footer.vue';
import LiveStreamAnnouncement from '@root/common/components/layout/LiveStreamAnnouncement.vue';
import { Header as IHeader, Footer as IFooter, Mobile as IMobile } from '@root/modules/navigation/types/navigation';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import AdFormSiteheader from '@ads/components/adForm/AdFormSiteheader.vue';
import AdFormPromotionLine from '@ads/components/adForm/AdFormPromotionLine.vue';
import cookieBot from '@root/common/utils/cookieBot';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';
import { formatAdSurround, resetFormatAdSurround } from '@root/modules/ads/utils/formatAdSurround';
import ScrollTop from '@root/common/components/base/ScrollTop.vue';
import isTildaArticle from '@root/modules/article/utils/isTildaArticle';
import isFullWidthArticle from '@root/modules/article/utils/isFullWidthArticle';
import AlertHandler from '@root/common/components/layout/AlertHandler.vue';
import { AlertComponentProps } from '@core/components/UI/alert/types';
import isMobileView from '@root/common/utils/isMobileView';

const PianoMaintenanceAlert = () => import('@piano/components/PianoMaintenanceAlert.vue');
const BigPlayer = () => import('@root/modules/podcast/components/player/BigPlayer.vue');
const DesktopPlayer = () => import('@root/modules/podcast/components/player/DesktopPlayer.vue');
const SmallPlayer = () => import('@root/modules/podcast/components/player/SmallPlayer.vue');

interface Computed {
  hideHeader: boolean;
  headerNavigation: IHeader;
  footerNavigation: IFooter;
  mobileNavigation: IMobile[];
  hideOrdering: boolean;
  alerts: AlertComponentProps[];
  adsPlacements: Record<string, any>;
  siteHeaderAd: string;
  mobileSiteHeaderAd: string;
  headerAd: Record<string, any>;
  floatingAd: Record<string, any>;
  scrollerAd: Record<string, any>;
  bottomAd: string;
  mobileinterstitialAd: string;
  promotionAd: Record<string, any>;
  mobilePromotionAd: Record<string, any>;
  mobileBottomAd: Record<string, any>;
  isDelfiFrontpage: boolean;
  isFrontpage: boolean;
  isArticle: boolean;
  isCrArticle: boolean;
  isTildaArticle: boolean;
  isLiveBlogArticle: boolean;
  isFullWidthArticle: boolean;
  refetch: boolean;
  disableConsentModal: boolean;
  showPianoMaintenanceAlert: boolean;
  showPlayer: boolean;
  showBigPlayer: boolean;
  isPodcastChannel: boolean;
  themeClass: string | null;
}

interface Data {
  bannerKeys: Record<string, string>;
  isMobileView: boolean;
  observer: MutationObserver | null;
}

interface Methods {
  resetAdFormKey: (index?: number, skipHeader?: boolean) => void;
  copyUrlInject: () => void;
  getKey: (params: Record<string, any>) => string;
}

interface DefaultWindow extends Window {
  _AutofillCallbackHandler: any;
  dataLayer: any;
}

declare const window: DefaultWindow;

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    AdFormSiteheader,
    AdFormBanner,
    AdFormPromotionLine,
    Header,
    HeaderBottom,
    HeaderPromotion,
    Footer,
    LiveStreamAnnouncement,
    ScrollTop,
    PianoMaintenanceAlert,
    BigPlayer,
    DesktopPlayer,
    SmallPlayer,
    AlertHandler,
  },
  data() {
    return {
      bannerKeys: {
        siteHeader: 'siteheader',
        header: 'header',
        floating: 'floating',
        mobileBottom: 'mobileBottom',
        mobileInterstitial: 'mobileInterstitial',
        mobilePageview: 'mobilePageview',
        mobilePageviewGoogle: 'mobilePageviewGoogle',
        bottom: 'bottom',
        promotion: 'promotion',
      },
      isMobileView: false,
      observer: null,
    };
  },
  head() {
    const { id, lang, locale } = this.$channelConfig('settings');
    const { cbid } = this.$channelConfig('analytics')!.cookieBot;
    const bodyClass = this.hideHeader || this.isTildaArticle ? 'no-header' : '';
    const script = [];

    if (!this.hideOrdering) {
      script.push({
        id: 'Cookiebot',
        src: 'https://consent.cookiebot.com/uc.js',
        defer: true,
        'data-cbid': cbid,
        'data-framework': 'TCFv2.2',
        'data-culture': locale.split('_')[0],
        'data-wipe-preferences': '0',
        'data-wipe-statistics': '0',
        'data-wipe-marketing': '0',
      });

      // Append CB configuration script
      script.push({
        hid: 'CookiebotConfiguration',
        id: 'CookiebotConfiguration',
        type: 'application/json',
        innerHTML: JSON.stringify({
          Frameworks: {
            IABTCF2: {
              AllowedGoogleACVendors: [
                43, 46, 55, 61, 70, 83, 89, 93, 108, 117, 122, 124, 135, 143, 144, 147, 149, 159, 192, 196, 211, 228, 230, 239, 259, 266, 286, 291, 311, 320,
                322, 323, 327, 367, 371, 385, 407, 415, 424, 430, 436, 445, 486, 491, 494, 495, 522, 523, 540, 550, 560, 568, 574, 576, 584, 587, 591, 737, 802,
                803, 820, 839, 864, 899, 904, 922, 931, 938, 959, 979, 981, 985, 1003, 1027, 1031, 1040, 1046, 1051, 1053, 1067, 1092, 1095, 1097, 1099, 1107,
                1109, 1135, 1143, 1149, 1152, 1162, 1166, 1186, 1188, 1205, 1215, 1226, 1227, 1230, 1252, 1268, 1270, 1276, 1284, 1290, 1301, 1307, 1312, 1329,
                1345, 1356, 1375, 1403, 1415, 1416, 1421, 1423, 1440, 1449, 1455, 1495, 1512, 1516, 1525, 1540, 1548, 1555, 1558, 1570, 1577, 1579, 1583, 1584,
                1603, 1616, 1638, 1651, 1653, 1659, 1667, 1677, 1678, 1682, 1697, 1699, 1703, 1712, 1716, 1721, 1725, 1732, 1745, 1750, 1765, 1782, 1786, 1800,
                1810, 1825, 1827, 1832, 1838, 1840, 1843, 1845, 1859, 1870, 1878, 1880, 1889, 1917, 1929, 1942, 1944, 1962, 1963, 1964, 1967, 1968, 1969, 1978,
                1985, 1987, 2003, 2008, 2027, 2035, 2039, 2047, 2052, 2056, 2064, 2068, 2072, 2074, 2088, 2090, 2103, 2107, 2109, 2115, 2124, 2130, 2133, 2135,
                2137, 2140, 2147, 2156, 2166, 2177, 2186, 2205, 2213, 2216, 2219, 2220, 2222, 2225, 2234, 2253, 2275, 2279, 2282, 2309, 2312, 2316, 2322, 2325,
                2328, 2331, 2335, 2336, 2343, 2354, 2358, 2359, 2370, 2376, 2377, 2387, 2400, 2403, 2405, 2407, 2411, 2414, 2416, 2418, 2425, 2440, 2447, 2461,
                2465, 2468, 2472, 2477, 2484, 2486, 2488, 2493, 2498, 2501, 2510, 2517, 2526, 2527, 2532, 2535, 2542, 2552, 2563, 2564, 2567, 2568, 2569, 2571,
                2572, 2575, 2577, 2583, 2584, 2596, 2604, 2605, 2608, 2609, 2610, 2612, 2614, 2621, 2627, 2628, 2629, 2633, 2636, 2642, 2643, 2645, 2646, 2650,
                2651, 2652, 2656, 2657, 2658, 2660, 2661, 2669, 2670, 2677, 2681, 2684, 2687, 2690, 2695, 2698, 2713, 2714, 2729, 2739, 2767, 2768, 2770, 2772,
                2784, 2787, 2791, 2792, 2798, 2801, 2805, 2812, 2813, 2816, 2817, 2821, 2822, 2827, 2830, 2831, 2833, 2834, 2838, 2839, 2844, 2846, 2849, 2850,
                2852, 2854, 2860, 2862, 2863, 2865, 2867, 2869, 2874, 2875, 2878, 2880, 2881, 2882, 2883, 2884, 2886, 2887, 2888, 2889, 2891, 2893, 2894, 2895,
                2897, 2898, 2900, 2901, 2908, 2909, 2916, 2917, 2918, 2920, 2922, 2923, 2927, 2929, 2930, 2931, 2940, 2941, 2947, 2949, 2950, 2956, 2958, 2961,
                2963, 2964, 2965, 2966, 2968, 2973, 2975, 2979, 2980, 2981, 2983, 2985, 2986, 2987, 2994, 2995, 2997, 2999, 3000, 3002, 3003, 3005, 3008, 3009,
                3010, 3012, 3016, 3017, 3018, 3019, 3028, 3034, 3038, 3043, 3052, 3053, 3055, 3058, 3059, 3063, 3066, 3068, 3070, 3073, 3074, 3075, 3076, 3077,
                3089, 3090, 3093, 3094, 3095, 3097, 3099, 3100, 3106, 3109, 3112, 3117, 3119, 3126, 3127, 3128, 3130, 3135, 3136, 3145, 3150, 3151, 3154, 3155,
                3163, 3167, 3172, 3173, 3182, 3183, 3184, 3185, 3187, 3188, 3189, 3190, 3194, 3196, 3209, 3210, 3211, 3214, 3215, 3217, 3222, 3223, 3225, 3226,
                3227, 3228, 3230, 3231, 3234, 3235, 3236, 3237, 3238, 3240, 3244, 3245, 3250, 3251, 3253, 3257, 3260, 3270, 3272, 3281, 3288, 3290, 3292, 3293,
                3296, 3299, 3300, 3306, 3307, 3309, 3314, 3315, 3316, 3318, 3324, 3328, 3330, 3331, 3531, 3731, 3831, 4131, 4531, 4631, 4731, 4831, 5231, 6931,
                7235, 7831, 7931, 8931, 9731, 10231, 10631, 10831, 11031, 11531, 13632, 13731, 14034, 14133, 14237, 14332, 15731, 16831, 16931, 21233, 23031,
                25131, 25931, 26031, 26631, 26831, 27731, 27831, 28031, 28731, 28831, 29631, 30532, 32531, 33931, 34231, 34631, 36831, 39131, 39531, 40632,
                41131, 41531, 43631, 43731, 43831, 45931, 47031, 47232, 47531, 48131, 49231, 49332, 49431, 50831, 52831, 54231, 56831, 56931, 57131,
              ],
            },
          },
        }),
      });
    }

    script.push({ src: '//s1.adform.net/banners/scripts/adx.js', async: true });

    // Inject ld+json into head
    const { base } = this.$channelConfig('meta');
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': id === 'fone' ? 'PodcastSeries' : 'WebSite',
      url: base.baseUrl,
      author: {
        '@type': 'Organization',
        name: 'Delfi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Delfi',
      },
      description: base.description,
    };

    script.push({ hid: 'default-ld-json', type: 'application/ld+json', json: jsonLd });

    return {
      htmlAttrs: {
        lang,
      },
      bodyAttrs: {
        class: bodyClass,
      },
      script,
      __dangerouslyDisableSanitizersByTagID: {
        CookiebotConfiguration: ['innerHTML'],
      },
    };
  },
  computed: {
    hideHeader() {
      const { specialFeatures } = this.$channelConfig('settings').application;
      return specialFeatures.disableHeader;
    },
    ...mapState({
      headerNavigation: (state: any) => state.navigation.header.data as IHeader,
      footerNavigation: (state: any) => state.navigation.footer.data as IFooter,
      mobileNavigation: (state: any) => state.navigation.mobile.data as IMobile[],
    }),
    hideOrdering() {
      const { specialFeatures } = this.$channelConfig('settings').application;
      return specialFeatures.hideSignupAndOrders.enabled;
    },
    alerts() {
      return this.$store.state.index.alerts || [];
    },
    adsPlacements() {
      const ads: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return ads || {};
    },
    siteHeaderAd() {
      return (this.adsPlacements.siteheader && this.adsPlacements.siteheader.mid[0]) || null;
    },
    mobileSiteHeaderAd() {
      return (this.adsPlacements.mobilesiteheader && this.adsPlacements.mobilesiteheader.mid[0]) || null;
    },
    headerAd() {
      const placement: Record<string, any> = { mid: null, adb: null };

      if (!this.adsPlacements.header) {
        return placement;
      }

      if (this.isDelfiFrontpage && this.adsPlacements.header.dfp && this.adsPlacements.header.dfp.mid && this.adsPlacements.header.dfp.mid.length) {
        placement.mid = this.adsPlacements.header.dfp.mid[0];

        if (this.adsPlacements.header.dfp.group) {
          placement.group = this.adsPlacements.header.dfp.group;
        }
      } else if (this.adsPlacements.header.mid.length) {
        placement.mid = this.adsPlacements.header.mid[0];

        if (this.adsPlacements.header.group) {
          placement.group = this.adsPlacements.header.group;
        }
      }

      if (this.adsPlacements.header.adb) {
        placement.adb = this.adsPlacements.header.adb;
      }

      return placement;
    },
    floatingAd() {
      const placement: Record<string, any> = { mid: null, adb: null };

      if (!this.adsPlacements.floating) {
        return placement;
      }

      if (this.isDelfiFrontpage && this.adsPlacements.floating.dfp && this.adsPlacements.floating.dfp.mid && this.adsPlacements.floating.dfp.mid.length) {
        placement.mid = this.adsPlacements.floating.dfp.mid[0];
      } else if (this.adsPlacements.floating.mid.length) {
        placement.mid = this.adsPlacements.floating.mid[0];
      }

      if (this.adsPlacements.floating.adb) {
        placement.adb = this.adsPlacements.floating.adb;
      }

      return placement;
    },
    scrollerAd() {
      const placement: Record<string, any> = { mid: null, adb: null };

      if (this.adsPlacements.scroller && this.adsPlacements.scroller.mid.length > 0 && this.adsPlacements.scroller.mid[0]) {
        placement.mid = this.adsPlacements.scroller.mid[0];
      }

      if (this.adsPlacements.scroller && this.adsPlacements.scroller.adb) {
        placement.adb = this.adsPlacements.scroller.adb;
      }

      return placement;
    },
    bottomAd() {
      return (this.adsPlacements.bottom && this.adsPlacements.bottom.mid[0]) || null;
    },
    mobileinterstitialAd() {
      return (this.adsPlacements.mobileinterstitial && this.adsPlacements.mobileinterstitial.mid[0]) || null;
    },
    promotionAd() {
      return (this.adsPlacements.promotion && this.adsPlacements.promotion.mid[0]) || null;
    },
    mobilePromotionAd() {
      return (this.adsPlacements.mobilepromotion && this.adsPlacements.mobilepromotion.mid[0]) || null;
    },
    mobileBottomAd() {
      return (this.adsPlacements.mobilebottom && this.adsPlacements.mobilebottom.mid[0]) || null;
    },
    isDelfiFrontpage() {
      return this.isFrontpage && this.$channelConfig('settings').id === 'delfi';
    },
    isFrontpage() {
      return this.$route.name === 'frontpage';
    },
    isArticle() {
      return this.$route.name === 'article';
    },
    isCrArticle() {
      if (!this.isArticle) {
        return false;
      }

      const activeArticle = this.$store.getters['article/getActiveArticle'];
      if (!activeArticle) {
        return false;
      }

      const crArticles = this.$store.getters['article/getCrArticles'];
      const isCrArticle = crArticles.find((article: number) => article === activeArticle.id);

      return this.isArticle && isCrArticle;
    },
    isTildaArticle() {
      if (!this.isArticle) {
        return false;
      }

      const customCategorySettings = this.$channelConfig('page').category?.component?.header;
      return isTildaArticle(this.$route, customCategorySettings);
    },
    isLiveBlogArticle() {
      if (!this.isArticle) {
        return false;
      }

      const activeArticle = this.$store.getters['article/getActiveArticle'];
      if (!activeArticle) {
        return false;
      }
      return activeArticle && activeArticle.settings?.articleTemplate && activeArticle.settings?.articleTemplate === 'liveBlog';
    },
    isFullWidthArticle() {
      if (!this.isArticle) {
        return false;
      }

      const activeArticle = this.$store.getters['article/getActiveArticle'];
      if (!activeArticle) {
        return false;
      }

      return isFullWidthArticle(activeArticle);
    },
    refetch() {
      return this.$store.state.frontpage.refetch;
    },
    disableConsentModal() {
      const { specialFeatures } = this.$channelConfig('settings').application;

      return specialFeatures.disableConsentModal;
    },
    showPianoMaintenanceAlert() {
      return this.$store.state.piano.maintenanceAlertVisible;
    },
    showPlayer() {
      const isPlaying = Boolean(this.$player?.state.currentlyPlaying.audioId);
      const lastPlayed = this.$player?.state.lastPlayed;

      // Setup player to resume playing
      if (process.client && !isPlaying && lastPlayed !== null) {
        this.$player?.setup(lastPlayed, true);
      }

      return isPlaying || (!isPlaying && lastPlayed !== null);
    },
    showBigPlayer() {
      return this.$player?.state.showBigPlayer;
    },
    isPodcastChannel() {
      return this.$channelConfig('settings').id === 'fone';
    },
    themeClass() {
      return this.$channelConfig('settings').theme !== 'delfi' ? `theme-${this.$channelConfig('settings').theme}` : null;
    },
  },
  watch: {
    // asyncData logic - key will change after page asyncData has finished
    $route() {
      if (process.client) {
        this.resetAdFormKey();
      }

      resetFormatAdSurround();

      if (process.client) {
        // Dispatch custom event for player.js
        window.dispatchEvent(new CustomEvent('nuxtRouteChange'));
      }
    },
    refetch(refetch) {
      if (refetch) {
        this.resetAdFormKey(Math.floor(10000 + Math.random() * 90000));
      }
    },
    showBigPlayer(isOpen) {
      if (isOpen) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    },
  },
  mounted() {
    this.isMobileView = isMobileView();

    // Cookiebot onLoad hook
    cookieBot();

    // Remove self-injected Google CMP container in review
    if (this.disableConsentModal || this.hideOrdering) {
      const mutationCallback = (mutations: any) => {
        const mutation = mutations[0] as MutationRecord;
        const nodes = mutation.addedNodes as NodeList;
        const element = nodes[0] as HTMLElement;

        if (element && element.nodeName === 'DIV' && element.className === 'fc-consent-root') {
          element.remove();
        }
      };

      this.observer = new MutationObserver(mutationCallback);
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    // deepcode ignore InsufficientPostmessageValidation: <validation is made in handler>
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.data === 'formatAdSurround') {
        formatAdSurround();
      }
    });

    // Mute unknown FB iOS app error
    // https://developers.facebook.com/community/threads/320013549791141
    window._AutofillCallbackHandler = window._AutofillCallbackHandler || function () {};

    try {
      this.$instanceConnector('headlines').listen('headlineClick', async ({ headline }) => {
        if (headline.clickData) {
          this.$event.send('link-click', { url: headline.href, type: headline.clickData.type });
        }
      });
    } catch (e) {
      this.$sentry.captureException(`Headline click fail: ${e}`, {
        tags: { 'process.server': process.server ? 'server' : 'client' },
        contexts: { vue: { componentName: 'default.vue layout' } },
      });
    }

    this.copyUrlInject();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
    resetAdFormKey(index = 0) {
      this.bannerKeys = {
        siteHeader: this.getKey({ prefix: 'siteheader', index }),
        header: this.getKey({ prefix: 'header', index }),
        floating: this.getKey({ prefix: 'floating', index }),
        mobileBottom: this.getKey({ prefix: 'mobileBottom', index }),
        mobileInterstitial: this.getKey({ prefix: 'mobileInterstitial', index }),
        bottom: this.getKey({ prefix: 'bottom', index }),
      };
    },
    copyUrlInject() {
      if (!process.client || !window) {
        return;
      }

      window.document.addEventListener('copy', () => {
        const sel = window.getSelection();

        if (!sel || sel.anchorNode?.nodeName !== '#text' || sel.toString().length < 32) {
          return;
        }

        const isPaid = this.$store.getters['article/getActiveArticle']?.content.paywall.enabled || false;
        if (sel.toString().length > 100 && isPaid) {
          sel.removeAllRanges();
        }

        const loc = this.$t('copy_inject_url.message');
        const xtext = '<br/><br/> ' + loc + '<a href="' + document.location.href + '">' + document.location.href + '</a><br/><br/>';

        const div = window.document.createElement('div');
        // deepcode ignore DOMXSS: <data is not user input>
        div.innerHTML = sel + xtext;
        div.style.position = 'absolute';
        div.style.left = '-99999px';

        // deepcode ignore DOMXSS: <data is not user input>
        document.body.appendChild(div);

        sel.selectAllChildren(div);

        window.setTimeout(function () {
          div.remove();
        }, 0);
      });
    },
  },
});
